import React, { useMemo } from "react"

import { TagDotsPreview } from "@/tags"
import {
  Badge,
  CopyableId,
  type ColumnDef,
  type SortDescriptor,
} from "@coval/ui"

import { EntityPickerDialog } from "@/components/entity-picker/EntityPickerDialog"
import type { Persona } from "@/types/api/Persona"
import { getAvatarColor, getAvatarUrl } from "@/utils/avatarIdentity"
import {
  formatFullTimestamp,
  formatRelativeTimestamp,
} from "@/utils/formatting/format-timestamp"

import { isBuiltInPersona, resolvePersonaLanguage } from "./persona-utils"

// Trimmed from the personas list page's set to what a picker needs — the prompt
// stays, since it is what distinguishes two personas at a glance.
const NAME_COLUMN_WIDTH = 240
const NAME_COLUMN_MIN_WIDTH = 200
const PROMPT_COLUMN_WIDTH = 240
const PROMPT_MIN_WIDTH = 160
const LANGUAGE_COLUMN_WIDTH = 110
const DATE_COLUMN_WIDTH = 100
const TAGS_COLUMN_WIDTH = 110
const ID_COLUMN_WIDTH = 104

const DEFAULT_SORT: SortDescriptor = {
  columnId: "display_name",
  direction: "asc",
}

// Column model kept at module scope (deps passed in) so the JSX cells don't
// trip react/no-unstable-nested-components; the component memoizes the result.
function buildPersonaPickerColumns({
  tagColorMap,
}: {
  tagColorMap?: Record<string, string | null | undefined>
}): ColumnDef<Persona>[] {
  return [
    {
      id: "display_name",
      header: "Persona Name",
      variant: "name",
      width: NAME_COLUMN_WIDTH,
      minWidth: NAME_COLUMN_MIN_WIDTH,
      sortable: true,
      enableResizing: false,
      cell: (persona) => (
        <div className="flex min-w-0 items-center gap-3">
          <div
            className="flex h-8 w-8 shrink-0 items-end justify-center overflow-hidden rounded-full"
            style={{ backgroundColor: getAvatarColor(persona.id) }}
          >
            <img
              src={persona.avatar_url || getAvatarUrl(persona.id)}
              className="h-full w-full rounded-full"
              alt=""
            />
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate font-semibold">
              {persona.display_name}
            </span>
            {isBuiltInPersona(persona) && (
              <Badge variant="outline" className="shrink-0 text-xs">
                Built-in
              </Badge>
            )}
          </div>
        </div>
      ),
    },
    {
      id: "simulated_user_prompt",
      header: "Prompt",
      width: PROMPT_COLUMN_WIDTH,
      minWidth: PROMPT_MIN_WIDTH,
      sortable: true,
      enableResizing: false,
      cell: (persona) => (
        <span className="min-w-0 truncate text-coval-text-muted">
          {persona.simulated_user_prompt || "No prompt"}
        </span>
      ),
    },
    {
      id: "language",
      header: "Language",
      width: LANGUAGE_COLUMN_WIDTH,
      sortable: true,
      enableResizing: false,
      cell: (persona) => (
        <span className="min-w-0 truncate text-coval-text-muted">
          {resolvePersonaLanguage(persona)}
        </span>
      ),
    },
    {
      id: "last_updated_at",
      header: "Updated",
      width: DATE_COLUMN_WIDTH,
      sortable: true,
      enableResizing: false,
      cell: (persona) => (
        <span
          className="whitespace-nowrap text-coval-text-muted"
          title={formatFullTimestamp(persona.last_updated_at)}
        >
          {formatRelativeTimestamp(persona.last_updated_at)}
        </span>
      ),
    },
    {
      id: "tags",
      header: "Tags",
      width: TAGS_COLUMN_WIDTH,
      align: "right",
      flex: false,
      enableResizing: false,
      cell: (persona) => (
        <div className="flex min-w-0 justify-end">
          <TagDotsPreview tags={persona.tags ?? []} tagColorMap={tagColorMap} />
        </div>
      ),
    },
    {
      id: "id",
      header: "ID",
      variant: "id",
      width: ID_COLUMN_WIDTH,
      pinned: "right",
      enableResizing: false,
      cell: (persona) => <CopyableId id={persona.id} label="Copy Persona ID" />,
    },
  ]
}

interface PersonaPickerDialogProps {
  isOpen: boolean
  onCancel: () => void
  onApply: () => void
  personas: Persona[]
  isLoading?: boolean
  selectedIds: string[]
  onSelectionChange: (ids: string[]) => void
  allowMultiple?: boolean
  tagColorMap?: Record<string, string | null | undefined>
  createAction?: React.ReactNode
}

// `tag:<value>` narrows the search to tags only, matching the syntax the
// previous persona dropdown supported.
const TAG_QUERY_PREFIX = /^tag:(.*)$/i

const matchesPersonaTag = (persona: Persona, tagQuery: string) =>
  (persona.tags ?? []).some((tag) => tag.toLowerCase().includes(tagQuery))

/** Matches one persona against an already trimmed, lowercased query. */
export function personaMatchesSearch(persona: Persona, query: string) {
  const tagFilterMatch = query.match(TAG_QUERY_PREFIX)
  if (tagFilterMatch) {
    const tagQuery = tagFilterMatch[1].trim()
    // A bare `tag:` shows everything until a tag value is typed.
    return tagQuery === "" || matchesPersonaTag(persona, tagQuery)
  }
  return [
    persona.display_name,
    persona.id,
    persona.simulated_user_prompt,
    resolvePersonaLanguage(persona),
    ...(persona.tags ?? []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(query)
}

export const PersonaPickerDialog: React.FC<PersonaPickerDialogProps> = ({
  isOpen,
  onCancel,
  onApply,
  personas,
  isLoading = false,
  selectedIds,
  onSelectionChange,
  allowMultiple = true,
  tagColorMap,
  createAction,
}) => {
  const columns = useMemo(
    () => buildPersonaPickerColumns({ tagColorMap }),
    [tagColorMap],
  )

  return (
    <EntityPickerDialog<Persona>
      isOpen={isOpen}
      onCancel={onCancel}
      onApply={onApply}
      title="Select Personas"
      items={personas}
      columns={columns}
      getItemId={(persona) => persona.id}
      getItemLabel={(persona) => persona.display_name || persona.id}
      matchesSearch={personaMatchesSearch}
      selectedIds={selectedIds}
      onSelectionChange={onSelectionChange}
      isLoading={isLoading}
      allowMultiple={allowMultiple}
      defaultSort={DEFAULT_SORT}
      rowAttributes={(persona) => ({
        "data-testid": `persona-option-${persona.id}`,
        "data-persona-name": persona.display_name || undefined,
      })}
      searchPlaceholder="Search personas by name, prompt, language, or tag:name..."
      createAction={createAction}
      emptyState="No personas yet"
      searchEmptyState="No personas match your search"
      data-testid="persona-picker-dialog"
    />
  )
}
